import { UserRole } from '@core/generated/gql-types';
import { isClientWebRole } from '@core/portal/clientPortal.util';
import type { PermissionsStatus } from '@core/permission/usePermissionStore';
import type {
  PortalAccessContext,
  PortalAudience,
} from '@/access/portalAccess.types';

type PermissionsInput = {
  permissions: string[];
  status: PermissionsStatus;
};

const NO_PERMISSIONS: PermissionsInput = { permissions: [], status: 'idle' };

export const getPortalAudience = ({
  isClientPortal,
}: Pick<PortalAccessContext, 'isClientPortal'>): PortalAudience =>
  isClientPortal ? 'client' : 'tenant';

const buildUnauthenticatedPortalAccessContext = (): PortalAccessContext => ({
  role: UserRole.Unknown,
  audience: 'client',
  isClientPortal: false,
  isTenantPortal: false,
  isHqAdmin: false,
  isTenantAdmin: false,
  isTenantUser: false,
  isTenantRecruiter: false,
  isClientAdmin: false,
  isClientUser: false,
  isPortalAdmin: false,
  canManageTenantJobs: false,
  canExportTenantReport: false,
  canCreateJob: false,
  permissions: [],
  permissionsStatus: 'idle',
  isClient: false,
  isAdmin: false,
});

/**
 * @param permissionsInput - backend permissions; omit for role-only checks
 * (permission-gated routes then read as not yet loaded).
 */
export const buildPortalAccessContext = (
  role: UserRole | null | undefined,
  permissionsInput: PermissionsInput = NO_PERMISSIONS,
): PortalAccessContext => {
  if (!role) {
    return buildUnauthenticatedPortalAccessContext();
  }

  const resolvedRole = role;
  const isClientPortal = isClientWebRole(resolvedRole);
  const isTenantPortal = !isClientPortal;
  const isHqAdmin = resolvedRole === UserRole.HqAdmin;
  const isTenantAdmin = resolvedRole === UserRole.TenantAdmin;
  const isTenantUser = resolvedRole === UserRole.TenantUser;
  const isTenantRecruiter = resolvedRole === UserRole.TenantRecruiter;
  const isClientAdmin = resolvedRole === UserRole.ClientAdmin;
  const isClientUser = resolvedRole === UserRole.ClientUser;
  const isPortalAdmin = isHqAdmin || isTenantAdmin;
  const canManageTenantJobs = isTenantPortal && !isTenantRecruiter;

  return {
    role: resolvedRole,
    audience: isClientPortal ? 'client' : 'tenant',
    isClientPortal,
    isTenantPortal,
    isHqAdmin,
    isTenantAdmin,
    isTenantUser,
    isTenantRecruiter,
    isClientAdmin,
    isClientUser,
    isPortalAdmin,
    canManageTenantJobs,
    canExportTenantReport: isPortalAdmin && isTenantPortal,
    canCreateJob: isClientPortal || canManageTenantJobs,
    permissions: permissionsInput.permissions,
    permissionsStatus: permissionsInput.status,
    isClient: isClientPortal,
    isAdmin: isPortalAdmin,
  };
};

/** @deprecated Use `buildPortalAccessContext` */
export const getPortalAccessFromRole = buildPortalAccessContext;
